import type { Client } from '@ooh/contracts';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import type { UseQuickAddCreateDraftFormReturn } from './quick-add-offer-picker-form';
import { QuickAddOfferPickerPanel } from './quick-add-offer-picker-panel';
import {
  QuickAddOfferPickerFooter,
  QuickAddOfferPickerHeader,
} from './quick-add-offer-picker-sections';
import type {
  QuickAddClientOption,
  QuickAddOfferPickerMode,
  QuickAddOfferPickerProps,
} from './quick-add-offer-picker';

const MODAL_MAX_WIDTH = 'max-w-xl';

/**
 * Extracted from quick-add-offer-picker.tsx to keep that file under the
 * repo's max-lines lint threshold — the `presentation="modal"` chrome
 * (`ModalDialog` header/footer/body wiring) around the shared panel.
 */
export function QuickAddOfferPickerModal({
  props,
  mode,
  selectedDraftId,
  createDraftForm,
  createDraftConfirmDisabled,
  salespersonFieldDisabled,
  createDraftSubmit,
  formId,
  salesUserOptions,
  clientOptions,
  selectedClientBrands,
  onClientCreated,
  addCoCreators,
  onAddCoCreatorsChange,
  onExistingConfirm,
  onModeChange,
  onSelectDraft,
  onOpenFullForm,
}: {
  props: QuickAddOfferPickerProps;
  mode: QuickAddOfferPickerMode;
  selectedDraftId: string | null;
  createDraftForm: UseQuickAddCreateDraftFormReturn['form'];
  createDraftConfirmDisabled: boolean;
  salespersonFieldDisabled: boolean;
  createDraftSubmit: () => void;
  formId: string;
  salesUserOptions: QuickAddClientOption[];
  clientOptions: QuickAddClientOption[];
  selectedClientBrands: string[];
  onClientCreated: (client: Client, wasCreated: boolean) => void;
  addCoCreators: boolean;
  onAddCoCreatorsChange: (checked: boolean) => void;
  onExistingConfirm: () => void;
  onModeChange: (mode: QuickAddOfferPickerMode) => void;
  onSelectDraft: (draftId: string | null) => void;
  onOpenFullForm: () => void;
}) {
  return (
    <ModalDialog
      testId="quick-add-offer-picker-modal"
      maxWidth={MODAL_MAX_WIDTH}
      closeLabel={props.cancelLabel}
      onClose={props.onCancel ?? (() => {})}
      header={<QuickAddOfferPickerHeader title={props.title} subtitle={props.subtitle} />}
      footer={
        <QuickAddOfferPickerFooter
          {...props}
          mode={mode}
          selectedDraftId={selectedDraftId}
          formId={formId}
          createDraftConfirmDisabled={createDraftConfirmDisabled}
          onCreateDraftSubmit={createDraftSubmit}
          onExistingConfirm={onExistingConfirm}
        />
      }
    >
      <QuickAddOfferPickerPanel
        {...props}
        onOpenFullForm={onOpenFullForm}
        selectedDraftId={selectedDraftId}
        mode={mode}
        createDraftForm={createDraftForm}
        formId={formId}
        createDraftConfirmDisabled={createDraftConfirmDisabled}
        salespersonFieldDisabled={salespersonFieldDisabled}
        salesUserOptions={salesUserOptions}
        clientOptions={clientOptions}
        selectedClientBrands={selectedClientBrands}
        onClientCreated={onClientCreated}
        addCoCreators={addCoCreators}
        onAddCoCreatorsChange={onAddCoCreatorsChange}
        onCreateDraftSubmit={createDraftSubmit}
        onExistingConfirm={onExistingConfirm}
        onModeChange={onModeChange}
        onSelectDraft={onSelectDraft}
        showFooter={false}
        showHeader={false}
      />
    </ModalDialog>
  );
}
